import { toast } from "react-toastify";
import { DateTime } from "luxon";
import {
  StatementRelativePeriods,
  StatementType,
  ACCOUNTING_CONFIG,
} from "shared";
import {
  useEffect,
  useMemo,
  useState,
} from "react";

import { env } from "../../../../../../common/lib/env";
import { useApiRequest } from "../../../../../hooks/useApiRequest";
import {  useContact, useContactStatement } from "../../../../../hooks/useContact";
import { useSettings } from "../../../../../hooks/useSettings";
import { useCurrentOrgTimezone } from "../../../../../hooks/useCurrentOrgTimezone";
import { Select } from "../../../../../../common/Atoms/Select";
import { MultiButtonCardHeader } from "../../../../../../common/Atoms/MultiButtonCardHeader";
import { useGetSelectedOrganisation } from "../../../../../hooks/useGetSelectedOrganisation";
import { DatePopover } from "../../../../../../common/Components/DatePopover";
import { Heading } from "../../../../../../common/Atoms/Typography/Heading";
import { Dropdown, DropdownOption } from "../../../../../../common/Atoms/Dropdown";
import { DatePicker } from "../../../../../../common/Atoms/DatePicker";
import { useAccountingSystemSetting } from '../../../../../hooks/useAccountingSystemSetting';

import { CurrentStatement } from "./CurrentStatement";
import { ActivityStatement } from "./ActivityStatement";

const periodOptions = [
  {
    label: `View all outstanding`,
    value: StatementRelativePeriods.ALL_TIME,
  },
  {
    label: `View all outstanding & future`,
    value: StatementRelativePeriods.ALL_TIME_INC_FUTURE,
  },
  {
    label: `Issued this month`,
    value: StatementRelativePeriods.THIS_MONTH,
  },
  {
    label: `Issued last Month`,
    value: StatementRelativePeriods.PREVIOUS_MONTH,
  },
  {
    label: `Issued upto end of last month`,
    value: StatementRelativePeriods.UPTO_PREVIOUS_MONTH,
  },
];

const statementTypeOptions = [
  {
    label: `Outstanding`,
    value: StatementType.OUTSTANDING,
  },
  {
    label: `Activity`,
    value: StatementType.ACTIVITY,
  },
];

export function ContactStatements() {
  const {
    data: statement,
    isFetching,
    isLoading,
    statementPeriod,
    currentCurrency,
    statementType,
    activityPeriodStart,
    activityPeriodEnd,
    setStatementPeriod,
    setCurrentCurrency,
    setStatementType,
    setActivityPeriodStart,
    setActivityPeriodEnd,
  } = useContactStatement();
  const { data: contact } = useContact();
  const possibleCurrencies = contact?.uniqueCurrencies || [];
  const request = useApiRequest();
  const { data: settings } = useSettings();
  const currentOrg = useGetSelectedOrganisation();
  const tz = useCurrentOrgTimezone();
  const [downloading, setDownloading] = useState(false);

  const canViewActivity = useAccountingSystemSetting(ACCOUNTING_CONFIG.SUPPORTS_ACTIVITY_STATEMENT);

  // Not every accounting system has activity statements - fall back to outstanding if the current org doesn't support them
  useEffect(() => {
    if (currentOrg && !canViewActivity && statementType === StatementType.ACTIVITY) {
      setStatementType(StatementType.OUTSTANDING);
    }
  }, [currentOrg, canViewActivity, statementType]);

  async function onDownload(type: StatementType) {
    try {
      setDownloading(true);

      const result = await request({
        method: `GET`,
        responseType: `blob`,
        url: `${env.baseApiUrl}contact/${statement.contact.id}/statement/download`,
        params: {
          period: statementPeriod,
          currency: currentCurrency,
          type,
        },
      });

      const url = window.URL.createObjectURL(new Blob([result.data]));
      const link = document.createElement(`a`);
      link.href = url;

      const endToUse = type === StatementType.ACTIVITY ? settings.activityEndDate : statement.period.end;
      const startToUse = type === StatementType.ACTIVITY ? settings.activityStartDate : statement.period.start;

      const fileNamePeriodEndSection = endToUse || ``;
      const fileNamePeriodStartSection = startToUse || ``;
      const divider = fileNamePeriodStartSection && fileNamePeriodEndSection ? `-` : ``;
      let fileNamePeriodSection = `${fileNamePeriodStartSection}${divider}${fileNamePeriodEndSection}`;

      if (!fileNamePeriodSection) {
        fileNamePeriodSection = DateTime.fromJSDate(new Date(statement.date), { zone: tz }).toFormat(`yyyy-MM-dd`);
      }

      link.setAttribute(`download`, `${statement.contact.name}_${fileNamePeriodSection}_${type}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setDownloading(false);
    }
    catch (e) {
      const responseObj = await e.response?.data?.text();
      toast.error(`${responseObj ? `${responseObj}` : `Could not create statement.`}`);
      setDownloading(false);
    }
  }

  // TODO - extract all this logic out to a resuable hook

  const actions = useMemo(() => {
    const actionArray: DropdownOption[] = [];

    if (onDownload) {
      if (canViewActivity) {
        actionArray.push({
          label: `Download Activity Statement`,
          tooltip: `Download Activity Statement as PDF`,
          onClick: () => onDownload(StatementType.ACTIVITY),
          loading: downloading,
        });
      }

      actionArray.push({
        label: `Download Outstanding Statement`,
        tooltip: `Download Outstanding Statement as PDF`,
        onClick: () => onDownload(StatementType.OUTSTANDING),
        loading: downloading,
      });
    }

    return actionArray;
  }, [onDownload, downloading, canViewActivity]);

  const isActivity = canViewActivity && statementType === StatementType.ACTIVITY;

  const relevantStatement = statement?.currencySections.find(section => section.currency === currentCurrency);

  if (!relevantStatement) {
    console.log(`No relevant statement found for currency ${currentCurrency}`);

    return null;
  }

  let statementPeriodText = null;

  if (isActivity && statement.type === StatementType.ACTIVITY) {
    statementPeriodText = (
      <div className={ `flex` }>
        { `All activity between ` }
        <DatePopover
          className={ `ml-1` }
          date={ statement.period.start }
          labelFormat={ `dd LLLL yyyy` }
          time={ `start` }
          organisationTimezone={ tz }
        />
        <span className={ `mx-1` }>{ `and` }</span>
        <DatePopover
          date={ statement.period.end }
          labelFormat={ `dd LLLL yyyy` }
          time={ `end` }
          organisationTimezone={ tz }
        />
      </div>
    );
  }
  else if (statement.period.start) {

    statementPeriodText = (
      <div className={ `flex` }>
        { `All items outstanding issued in ` }
        <DatePopover
          className={ `ml-1` }
          date={ statement.period.start }
          labelFormat={ `LLLL yyyy` }
          time={ `start` }
          monthOnly
          organisationTimezone={ tz }
        />
      </div>
    );
  }
  else if (statement.period.end) {
    statementPeriodText = (
      <div className={ `flex` }>
        { `All items outstanding issued on or before ` }
        <DatePopover
          className={ `ml-1` }
          date={ statement.period.end }
          labelFormat={ `dd LLLL yyyy` }
          time={ `end` }
          organisationTimezone={ tz }
        />
      </div>

    );
  }

  return (
    <div>
      <MultiButtonCardHeader
        className={ `p-0 sm:p-0 pb-2 sm:pb-2` }
        description={ statementPeriodText }
        cta= { (
          <div className={ `flex items-center gap-2` }>
            { canViewActivity && (
              <div className={ `max-w-[160px]` }>
                <Select
                  selected={ statementType }
                  onChange={ e => setStatementType(e.value as StatementType) }
                  options={ statementTypeOptions }
                  size={ `sm` }
                />
              </div>
            ) }
            { isActivity ? (
              <div className={ `flex items-center gap-2` }>
                <DatePicker
                  value={ activityPeriodStart || (statement.type === StatementType.ACTIVITY ? statement.period.start : null) || null }
                  onChange={ ({ value }) => setActivityPeriodStart(value) }
                  placeholder={ `From` }
                />
                <DatePicker
                  value={ activityPeriodEnd || (statement.type === StatementType.ACTIVITY ? statement.period.end : null) || null }
                  onChange={ ({ value }) => setActivityPeriodEnd(value) }
                  placeholder={ `To` }
                />
              </div>
            ) : (
              <div className={ `max-w-[200px]` }>
                <Select
                  selected={ statementPeriod }
                  onChange={ e => setStatementPeriod(e.value as StatementRelativePeriods) }
                  options={ periodOptions }
                  size={ `sm` }
                />
              </div>
            ) }
          </div>
        ) }
        buttonDropdownTheme={ `outline` }
        title={ (
          <div className={ `flex items-center` }>
            <Heading>
              {
                isActivity ? `Activity Statement` : `Outstanding Statement`
              }
            </Heading>
            <div className={ `max-w-[120px] ml-4` }>
              <Select
                selected={ currentCurrency }
                onChange={ e => setCurrentCurrency(e.value) }
                options={ possibleCurrencies.map(currency => ({ label: currency, value: currency })) }
                style={ `light` }
                size={ `sm` }
              />
            </div>
          </div>
        ) }
      >
        <div className={ `flex justify-end` }>
          <Dropdown
            options={ [actions] }
            label={ `Download` }
            theme={ `outline` }
            position={ `left` }
            size={ `sm` }
          />
        </div>
      </MultiButtonCardHeader>
      { isActivity && statement.type === StatementType.ACTIVITY ? (
        <ActivityStatement
          currencySection={ relevantStatement }
          loading={ isFetching || isLoading }
          organisationTimezone={ tz }
        />
      ) : (
        <CurrentStatement
          currencySection={ relevantStatement }
          loading={ isFetching || isLoading }
          organisationTimezone={ tz }
          omitColumns={ [ `payments` ] }
          childContacts = { statement.contact.childContacts }
        />
      ) }
    </div>
  );
}
